/**
 * Bottoni d'azione 360 (da usare in `customActionComponents` del dettaglio) + orchestrazione
 * del flusso post-creazione.
 *
 * Azioni dirette:
 *  - "Importa prodotti" (solo Company360) → apre ImportGoods360Modal
 *  - "Relazioni" → apre Relations360Modal
 *
 * Flusso a step (succession): dopo la creazione il configuratore reindirizza al dettaglio con
 * `?flow=postcreate`. In quel caso apriamo i modali in sequenza:
 *  - Company360: import prodotti → relazioni
 *  - Good360 / Trend360: relazioni
 * Aprendo i modali dai bottoni, invece, NON si innesca alcuno step successivo.
 */

import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { t } from '../utils/labels';
import { ENTITY } from '../utils/entities';
import { Relations360Modal } from './Relations360Modal';
import { Good360RelationsModal } from './Good360RelationsModal';
import { Relations360Dialog } from './relations360Shared';
import { ImportGoods360Modal } from './ImportGoods360Modal';

const FLOW_PARAM = 'flow';
const FLOW_VALUE = 'postcreate';

// Flussi post-creazione già avviati in questa sessione (per id del record). Evita che un
// eventuale remount del dettaglio (vedi nota sotto) riavvii il flusso.
const startedFlows = new Set<string>();

type ActiveModal = 'none' | 'import' | 'relations';

interface Entity360ActionsProps {
	entity: any;
	entityName: ENTITY;
}

export const Entity360Actions: React.FC<Entity360ActionsProps> = ({
	entity,
	entityName,
}) => {
	const location = useLocation();
	const supportsImport = entityName === ENTITY.COMPANY_360;

	const [active, setActive] = useState<ActiveModal>('none');
	const [inFlow, setInFlow] = useState(false);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const key = (entity && entity.id) || '';
		if (params.get(FLOW_PARAM) === FLOW_VALUE && !startedFlows.has(key)) {
			startedFlows.add(key);

			// Pulisco la query string SENZA passare da react-router: in SidebarLayout le Route
			// di dettaglio usano `component={inlineArrow}` e LoggedLayout ri-renderizza ad ogni
			// cambio di location, quindi una history.replace() di react-router rimonterebbe
			// l'intero dettaglio (e questo componente), perdendo lo stato del modale appena aperto.
			// window.history.replaceState aggiorna solo la barra degli indirizzi (refresh-safe)
			// senza notificare react-router, quindi nessun remount.
			params.delete(FLOW_PARAM);
			const search = params.toString();
			const newUrl =
				location.pathname +
				(search ? `?${search}` : '') +
				(location.hash || '');
			window.history.replaceState(window.history.state, '', newUrl);

			setInFlow(true);
			setActive(supportsImport ? 'import' : 'relations');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// In modalità flusso, la chiusura dell'import (salvataggio o annulla) porta alle relazioni;
	// in modalità diretta chiude e basta.
	const closeImport = () => setActive(inFlow ? 'relations' : 'none');
	const closeRelations = () => {
		setActive('none');
		setInFlow(false);
	};

	const openImport = () => {
		setInFlow(false);
		setActive('import');
	};
	const openRelations = () => {
		setInFlow(false);
		setActive('relations');
	};

	// Il dialogo "Dati e relazioni" dipende dal tipo: Company360 e Good360 hanno sezioni edit
	// proprie; gli altri (es. Trend360) usano la shell generica senza sezioni edit.
	const relationsProps = {
		entity,
		entityName,
		isOpen: active === 'relations',
		onClose: closeRelations,
	};
	const RelationsModalByType =
		entityName === ENTITY.COMPANY_360
			? Relations360Modal
			: entityName === ENTITY.GOOD_360
			? Good360RelationsModal
			: Relations360Dialog;

	return (
		<>
			{supportsImport && (
				<Button color="warning" className="margin-right" onClick={openImport}>
					<i className="fa fa-download" /> {t`ImportGoods360/button`}
				</Button>
			)}
			<Button color="info" className="margin-right" onClick={openRelations}>
				<i className="fa fa-link" /> {t`Relations360/button`}
			</Button>

			{supportsImport && (
				<ImportGoods360Modal
					company360Id={entity.id}
					isOpen={active === 'import'}
					onClose={closeImport}
					onSaved={closeImport}
				/>
			)}
			<RelationsModalByType {...relationsProps} />
		</>
	);
};